import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { QA_CRITERIA, qaTotal } from "@goyone/shared";
import { api, qs } from "../../lib/api";
import { dateTime, relative } from "../../lib/format";
import { useAuth } from "../../auth/AuthProvider";
import { Badge, EmptyState, Modal, PageHead } from "../../components/ui";
import { Icon, type IconName } from "../../components/Icon";
import { OwnerChip } from "../../components/People";
import { CallRecordingPlayer } from "../../components/CallRecording";
import { useToast } from "../../components/Toast";

/**
 * جودة المكالمات — قناتان لا واحدة.
 *
 * **تقييم المشرف**: داخليّ على سبعة معايير أوزانها مئة، غرضه التدريب —
 * والموظف يرى تقييمه وملاحظات مشرفه فورًا، فتقييمٌ لا يصل صاحبه لا
 * يدرّب أحدًا.
 *
 * **تقييم العميل**: صوت العميل نفسه — رابطٌ عام يُرسَل له بعد المكالمة
 * فيترك نجومًا من خمس وتعليقًا. الموظف يولّد روابط مكالماته هو،
 * والمشرف يولّد للجميع.
 *
 * المؤشرات أعلى الصفحة من آخر مئة تقييم في كل قناة — لا من عيّنة أصغر
 * تُحرّف المتوسط، ولا من ادّعاء «الكل» وقد اقتُطع.
 */
export function CallQuality() {
  const { atLeast, session } = useAuth();
  const isManager = atLeast("MANAGER");
  const userId = session!.user.id;
  const [reviewing, setReviewing] = useState<Record<string, any> | null>(null);

  const reviews = useQuery({
    queryKey: ["cc", "reviews"],
    queryFn: () =>
      api<{ items: Array<Record<string, any>> }>("/callcenter/reviews"),
  });

  const ratings = useQuery({
    queryKey: ["cc", "ratings"],
    queryFn: () =>
      api<{ items: Array<Record<string, any>> }>("/callcenter/ratings"),
  });

  /*
   * قائمة «اطلب رأي العميل»: مكالماتٌ تمّت يُولَّد لها رابط.
   * المشرف يرى الكل والموظف مكالماته — والخادم يضمّ حال التقييم لكل صفّ.
   */
  const completed = useQuery({
    queryKey: ["cc", "completed-for-rating", isManager ? "all" : userId],
    queryFn: () =>
      api<{ items: Array<Record<string, any>> }>(
        `/callcenter/calls${qs({
          status: "COMPLETED",
          pageSize: 25,
          ...(isManager ? {} : { agentId: userId }),
        })}`,
      ),
  });

  /* ---------- المؤشرات — من القوائم المعروضة نفسها ---------- */
  const reviewRows = reviews.data?.items ?? [];
  const ratingRows = ratings.data?.items ?? [];
  const submitted = ratingRows.filter((r) => r.score != null);
  const reviewAvg = reviewRows.length
    ? Math.round(
        reviewRows.reduce((n, r) => n + r.total, 0) / reviewRows.length,
      )
    : null;
  const csatAvg = submitted.length
    ? (submitted.reduce((n, r) => n + r.score, 0) / submitted.length).toFixed(1)
    : null;
  const responseRate = ratingRows.length
    ? Math.round((submitted.length / ratingRows.length) * 100)
    : null;

  /*
   * توزيعُ النجوم لا متوسّطُها وحده.
   *
   * متوسّطُ أربعةٍ من خمسة يخفي فرقًا بين «كلُّهم أعطوا أربعًا» و«نصفُهم
   * خمسًا ونصفُهم ثلاثًا» — والثاني فيه غاضبون يجب أن يُروا.
   */
  const starBars = [5, 4, 3, 2, 1].map((n) => ({
    star: n,
    count: submitted.filter((r) => Math.round(r.score) === n).length,
  }));
  const maxStar = Math.max(1, ...starBars.map((b) => b.count));

  /*
   * أضعفُ المعايير — من التقييمات المعروضة نفسِها.
   *
   * «متوسّطُك ٨٢» لا يقول ماذا تُصلح. ومعيارٌ متكرّرُ الضعف يقولها:
   * التدريبُ يبدأ من أدنى ثلاثةٍ لا من الرقم الجامع.
   */
  const criteria = QA_CRITERIA.map((c) => {
    const scored = reviewRows
      .map((r) => (r.scores as Record<string, number> | null)?.[c.key])
      .filter((v): v is number => typeof v === "number");
    const avg = scored.length
      ? scored.reduce((a, b) => a + b, 0) / scored.length
      : null;
    /* درجةُ المعيار من مئةٍ أصلًا — والوزنُ يُستعمل في الجمع لا في العرض. */
    return {
      ...c,
      avg,
      pct: avg === null ? null : Math.round(Math.max(0, Math.min(100, avg))),
    };
  })
    .filter((c) => c.pct !== null)
    .sort((a, b) => (a.pct ?? 0) - (b.pct ?? 0));

  /* لوحُ الموظفين — للمشرف وحده: من يحتاج تدريبًا يُرى بنظرة. */
  const byAgent = new Map<string, { review: number[]; rating: number[] }>();
  for (const r of reviewRows) {
    const id = r.call?.agentId ?? r.agentId;
    if (!id) continue;
    const row = byAgent.get(id) ?? { review: [], rating: [] };
    row.review.push(r.total);
    byAgent.set(id, row);
  }
  for (const r of submitted) {
    const id = r.call?.agentId;
    if (!id) continue;
    const row = byAgent.get(id) ?? { review: [], rating: [] };
    row.rating.push(r.score);
    byAgent.set(id, row);
  }
  const agentRows = [...byAgent.entries()]
    .map(([agentId, v]) => ({
      agentId,
      reviewAvg: v.review.length
        ? Math.round(v.review.reduce((a, b) => a + b, 0) / v.review.length)
        : null,
      reviewCount: v.review.length,
      ratingAvg: v.rating.length
        ? Number(
            (v.rating.reduce((a, b) => a + b, 0) / v.rating.length).toFixed(1),
          )
        : null,
      ratingCount: v.rating.length,
    }))
    .sort((a, b) => (b.reviewAvg ?? -1) - (a.reviewAvg ?? -1));

  return (
    <div className="page">
      <PageHead
        title="جودة المكالمات"
        subtitle={
          isManager
            ? "قناتان: تقييمك على سبعة معايير للتدريب، ورأي العميل بنجومٍ من رابط يُرسَل له"
            : "تقييمات مشرفك للتدريب — وآراء عملائك من روابط التقييم التي ترسلها"
        }
      />

      {/*
        صدرُ الجودة: رقمان يقودان الصفحة — تقييمُ المشرف ورأيُ العميل —
        وبجوارهما ما يفسّرهما: توزيعُ النجوم، ونسبةُ من أجاب.
      */}
      <div className="qa-hero">
        <div className="qa-score">
          <span
            className="qa-score-ring"
            style={{ ["--qa-pct" as string]: `${reviewAvg ?? 0}%` }}
          >
            <strong className="num">
              {reviewAvg !== null ? reviewAvg : "—"}
            </strong>
            <span className="tiny">من ١٠٠</span>
          </span>
          <div className="qa-score-text">
            <strong>تقييم المشرف</strong>
            <span className="tiny mute">
              {reviewRows.length
                ? `من ${reviewRows.length} تقييمًا للتدريب`
                : "لا تقييمات بعد"}
            </span>
          </div>
        </div>

        <div className="qa-score">
          <span
            className="qa-score-ring is-csat"
            style={{
              ["--qa-pct" as string]: `${((Number(csatAvg) || 0) / 5) * 100}%`,
            }}
          >
            <strong className="num">{csatAvg ?? "—"}</strong>
            <span className="tiny">من ٥</span>
          </span>
          <div className="qa-score-text">
            <strong>رضا العملاء</strong>
            <span className="tiny mute">
              {submitted.length
                ? `صوتُ ${submitted.length} عميلًا`
                : "لا آراء بعد"}
            </span>
          </div>
        </div>

        {/* توزيعُ النجوم: المتوسّطُ يخفي الغاضبين، والأعمدةُ تُظهرهم. */}
        <div className="qa-bars">
          <span className="tiny mute">توزيع النجوم</span>
          {starBars.map((b) => (
            <div className="qa-bar" key={b.star}>
              <span className="qa-bar-n num">{b.star}★</span>
              <span className="qa-bar-track">
                <span
                  className={`qa-bar-fill${b.star >= 4 ? " is-good" : b.star === 3 ? " is-mid" : " is-bad"}`}
                  style={{ width: `${(b.count / maxStar) * 100}%` }}
                />
              </span>
              <span className="qa-bar-c num tiny">{b.count}</span>
            </div>
          ))}
          <span className="tiny mute qa-bars-foot">
            {responseRate !== null
              ? `أجاب ${submitted.length} من ${ratingRows.length} رابطًا (${responseRate}٪)`
              : "لم تُرسَل روابط تقييمٍ بعد"}
          </span>
        </div>
      </div>

      {/* أضعفُ المعايير: التدريبُ يبدأ من أدناها لا من الرقم الجامع. */}
      {criteria.length > 0 && (
        <div className="card mb-4">
          <div className="card-head">
            <h2 className="card-title">
              <Icon name="target" size={15} /> أين يقع الضعف؟
            </h2>
            <span className="tiny mute">
              من {reviewRows.length} تقييمًا — الأدنى أولًا
            </span>
          </div>
          <div className="card-body">
            <div className="qa-crit">
              {criteria.slice(0, 4).map((c) => (
                <div className="qa-crit-row" key={c.key}>
                  <span className="qa-crit-label">{c.label}</span>
                  <span className="qa-bar-track">
                    <span
                      className={`qa-bar-fill${(c.pct ?? 0) >= 80 ? " is-good" : (c.pct ?? 0) >= 60 ? " is-mid" : " is-bad"}`}
                      style={{ width: `${c.pct}%` }}
                    />
                  </span>
                  <span className="num qa-crit-pct">{c.pct}٪</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* لوحُ الفريق — للمشرف: من يحتاج تدريبًا يُرى بنظرة. */}
      {isManager && agentRows.length > 0 && (
        <div className="card mb-4">
          <div className="card-head">
            <h2 className="card-title">
              <Icon name="users" size={15} /> جودة الفريق
            </h2>
          </div>
          <div className="card-body">
            <div className="table-wrap qa-scroll">
              <table className="table">
                <thead>
                  <tr>
                    <th>الموظف</th>
                    <th>تقييم المشرف</th>
                    <th>رضا العميل</th>
                    <th>عدد التقييمات</th>
                  </tr>
                </thead>
                <tbody>
                  {agentRows.map((a) => (
                    <tr key={a.agentId}>
                      <td>
                        <OwnerChip ownerId={a.agentId} size={24} />
                      </td>
                      <td>
                        {a.reviewAvg !== null ? (
                          <Badge
                            kind={
                              a.reviewAvg >= 80
                                ? "success"
                                : a.reviewAvg >= 60
                                  ? "warning"
                                  : "danger"
                            }
                          >
                            {a.reviewAvg}/100
                          </Badge>
                        ) : (
                          <span className="tiny mute">—</span>
                        )}
                      </td>
                      <td>
                        {a.ratingAvg !== null ? (
                          <span className="row" style={{ gap: 6 }}>
                            <Stars value={Math.round(a.ratingAvg)} size={12} />
                            <span className="num tiny">{a.ratingAvg}</span>
                          </span>
                        ) : (
                          <span className="tiny mute">—</span>
                        )}
                      </td>
                      <td className="num tiny mute">
                        {a.reviewCount} مشرف · {a.ratingCount} عميل
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <div className="two-col split">
        <div className="col" style={{ gap: "var(--sp-4)" }}>
          {/* ---------------- تقييم المشرف ---------------- */}
          {isManager && (
            <PendingReviews
              onPick={setReviewing}
              completed={completed.data?.items ?? []}
            />
          )}

          <div className="card">
            <div className="card-head">
              <h2 className="card-title">
                {isManager ? "تقييمات المشرف الأخيرة" : "تقييمات مشرفك"}
              </h2>
            </div>
            <div className="card-body">
              {reviewRows.length === 0 && (
                <EmptyState
                  title="لا تقييمات بعد"
                  text={
                    isManager
                      ? "اختر مكالمةً من قائمة الانتظار وقيّمها."
                      : "حين يقيّم مشرفك إحدى مكالماتك تظهر هنا بملاحظاتها."
                  }
                />
              )}
              <div className="col qa-scroll" style={{ gap: "var(--sp-3)" }}>
                {reviewRows.map((r) => (
                  <ReviewCard key={r.id} review={r} />
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="col" style={{ gap: "var(--sp-4)" }}>
          {/* ---------------- تقييم العملاء ---------------- */}
          <RequestRating calls={completed.data?.items ?? []} />

          <div className="card">
            <div className="card-head">
              <h2 className="card-title">
                {isManager ? "آراء العملاء" : "آراء عملائك"}
              </h2>
            </div>
            <div className="card-body">
              {submitted.length === 0 && (
                <EmptyState
                  title="لا آراء بعد"
                  text="أرسل رابط التقييم بعد المكالمة — واتساب أو بريدًا أو SMS — وما يجيب به العميل يظهر هنا."
                />
              )}
              <div className="col qa-scroll" style={{ gap: "var(--sp-3)" }}>
                {submitted.map((r) => (
                  <div key={r.id} className="cc-review">
                    <div className="row" style={{ gap: "var(--sp-2)" }}>
                      <Stars value={r.score} />
                      <div className="col" style={{ gap: 2, minWidth: 0 }}>
                        <strong className="small">
                          {r.call?.lead?.name ??
                            r.call?.contact?.name ??
                            r.call?.account?.name ??
                            r.call?.phone}
                        </strong>
                        <span className="tiny mute">
                          {relative(r.submittedAt)}
                        </span>
                      </div>
                      <span style={{ marginInlineStart: "auto" }}>
                        <OwnerChip
                          ownerId={r.call?.agentId}
                          size={22}
                          showName={false}
                        />
                      </span>
                    </div>
                    {r.comment && (
                      <p className="small cc-feedback">{r.comment}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {reviewing && (
        <ReviewDialog call={reviewing} onClose={() => setReviewing(null)} />
      )}
    </div>
  );
}

/* ==========================================================================
   النجوم
   ========================================================================== */

/** رأيُ العميل نجومًا — تُقرأ بالعين قبل الرقم. */
function Stars({ value, size = 15 }: { value: number; size?: number }) {
  return (
    <span
      className="cc-stars"
      title={`${value} من ٥`}
      aria-label={`${value} من ٥`}
    >
      {[1, 2, 3, 4, 5].map((n) => (
        <span key={n} className={`cc-star${value >= n ? " on" : ""}`}>
          <Icon name="star" size={size} />
        </span>
      ))}
    </span>
  );
}

/* ==========================================================================
   بانتظار تقييم المشرف
   ========================================================================== */

function PendingReviews({
  completed,
  onPick,
}: {
  completed: Array<Record<string, any>>;
  onPick: (call: Record<string, any>) => void;
}) {
  const pending = completed.filter((c) => !c.review);

  return (
    <div className="card">
      <div className="card-head">
        <h2 className="card-title">
          بانتظار تقييمك
          {pending.length > 0 && <Badge kind="warning">{pending.length}</Badge>}
        </h2>
      </div>
      <div className="card-body">
        {pending.length === 0 && (
          <p className="mute center">كل المكالمات المكتملة الأخيرة قُيّمت.</p>
        )}
        <div className="col qa-scroll" style={{ gap: 0 }}>
          {pending.map((c) => (
            <button
              key={c.id}
              className="cc-recent-row cc-rowbtn"
              onClick={() => onPick(c)}
            >
              <OwnerChip ownerId={c.agentId} size={22} />
              <span className="cc-recent-who">
                {c.lead?.name ?? c.contact?.name ?? c.account?.name ?? c.phone}
              </span>
              {c.disposition && (
                <Badge kind={c.disposition.kind}>{c.disposition.label}</Badge>
              )}
              <span className="tiny mute" style={{ marginInlineStart: "auto" }}>
                {dateTime(c.startedAt)}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ==========================================================================
   اطلب رأي العميل — توليد الرابط ونسخه
   ========================================================================== */

function RequestRating({ calls }: { calls: Array<Record<string, any>> }) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const mint = useMutation({
    mutationFn: (callId: string) =>
      api<{ token: string }>(`/callcenter/calls/${callId}/rating-link`, {
        method: "POST",
        body: {},
      }),
    onSuccess: async (res) => {
      const url = `${window.location.origin}/rate/${res.token}`;
      try {
        await navigator.clipboard.writeText(url);
        toast("نُسخ رابط التقييم — أرسله للعميل من واتساب أو البريد أو SMS");
      } catch {
        window.prompt("انسخ الرابط:", url);
      }
      void queryClient.invalidateQueries({ queryKey: ["cc"] });
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "تعذّر توليد الرابط."),
  });

  return (
    <div className="card">
      <div className="card-head">
        <h2 className="card-title">
          اطلب رأي العميل
          {calls.length > 0 && <Badge kind="info">{calls.length}</Badge>}
        </h2>
      </div>
      <div className="card-body">
        <p className="tiny mute mb-3">
          انسخ رابط التقييم وأرسله بعد المكالمة — يفتح صفحةً بلا تسجيل، نجومٌ
          وتعليق.
        </p>
        {calls.length === 0 && (
          <p className="mute center">لا مكالمات مكتملة بعد.</p>
        )}
        <div className="col" style={{ gap: 0 }}>
          {calls.slice(0, 10).map((c) => (
            <div key={c.id} className="cc-recent-row">
              <OwnerChip ownerId={c.agentId} size={22} showName={false} />
              <span className="cc-recent-who">
                {c.lead?.name ?? c.contact?.name ?? c.account?.name ?? c.phone}
              </span>

              {/* حال الرابط بصدق: لم يُرسَل / أُرسل ولم يُجَب / أُجيب بنجومه. */}
              {c.rating?.score != null ? (
                <Stars value={c.rating.score} size={13} />
              ) : c.rating ? (
                <span className="tiny mute">أُرسل — لم يُجب بعد</span>
              ) : null}

              <span style={{ marginInlineStart: "auto" }}>
                <button
                  className="btn btn-sm btn-ghost"
                  disabled={mint.isPending || c.rating?.score != null}
                  onClick={() => mint.mutate(c.id)}
                  title={
                    c.rating?.score != null
                      ? "قيّمها العميل بالفعل"
                      : "انسخ رابط التقييم"
                  }
                >
                  <Icon name="link" size={13} />
                  {c.rating ? "انسخ الرابط" : "رابط التقييم"}
                </button>
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ==========================================================================
   بطاقة تقييم المشرف — بمعاييرها عند الفتح
   ========================================================================== */

function ReviewCard({ review: r }: { review: Record<string, any> }) {
  const [open, setOpen] = useState(false);
  const scores = (r.scores ?? {}) as Record<string, number>;

  return (
    <div className="cc-review">
      <button
        className="row cc-rowbtn"
        style={{ gap: "var(--sp-2)", width: "100%" }}
        onClick={() => setOpen((v) => !v)}
      >
        <span
          className={`cc-score${r.total >= 70 ? " good" : r.total >= 50 ? " mid" : " low"} num`}
        >
          {r.total}
        </span>
        <div
          className="col"
          style={{ gap: 2, minWidth: 0, textAlign: "start" }}
        >
          <strong className="small">
            {r.call?.lead?.name ?? r.call?.account?.name ?? r.call?.phone}
          </strong>
          <span className="tiny mute">{dateTime(r.call?.startedAt)}</span>
        </div>
        <span style={{ marginInlineStart: "auto" }}>
          <OwnerChip ownerId={r.call?.agentId} size={22} showName={false} />
        </span>
        <Icon name={open ? "chevronUp" : "chevronDown"} size={14} />
      </button>

      {/* المعايير السبعة بدرجاتها — التدريب في التفصيل لا في الرقم الكلي. */}
      {open && (
        <div className="cc-crit-grid">
          {QA_CRITERIA.map((c) => {
            const v = scores[c.key] ?? 0;
            return (
              <div key={c.key} className="cc-crit">
                <span className="tiny">{c.label}</span>
                <span className="cc-crit-bar">
                  <span
                    className={`cc-crit-fill${v >= 70 ? " good" : v >= 50 ? " mid" : " low"}`}
                    style={{ width: `${v}%` }}
                  />
                </span>
                <span className="tiny num">{v}</span>
              </div>
            );
          })}
        </div>
      )}

      {r.feedback && <p className="small cc-feedback">{r.feedback}</p>}
    </div>
  );
}

/* ==========================================================================
   نافذة التقييم
   ========================================================================== */

function ReviewDialog({
  call,
  onClose,
}: {
  call: Record<string, any>;
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [scores, setScores] = useState<Record<string, number>>(
    Object.fromEntries(QA_CRITERIA.map((c) => [c.key, 70])),
  );
  const [feedback, setFeedback] = useState("");
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      api(`/callcenter/calls/${call.id}/review`, {
        method: "POST",
        body: { scores, feedback: feedback.trim() || undefined },
      }),
    onSuccess: () => {
      toast("حُفظ التقييم ووصل الموظفَ إشعارٌ به");
      void queryClient.invalidateQueries({ queryKey: ["cc"] });
      onClose();
    },
    onError: (e) => setError(e instanceof Error ? e.message : "تعذّر الحفظ."),
  });

  const total = qaTotal(scores);

  return (
    <Modal
      title={`تقييم مكالمة — ${call.lead?.name ?? call.account?.name ?? call.phone}`}
      onClose={onClose}
      footer={
        <>
          <span
            className={`cc-score${total >= 70 ? " good" : total >= 50 ? " mid" : " low"} num me-auto`}
          >
            {total}/100
          </span>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            disabled={save.isPending}
            onClick={() => save.mutate()}
          >
            حفظ التقييم
          </button>
        </>
      }
    >
      {error && <div className="alert alert-danger mb-3">{error}</div>}

      {call.recordingUrl && !String(call.recordingUrl).startsWith("http") && (
        <div className="mb-3">
          <CallRecordingPlayer id={call.recordingUrl} title="تسجيل المكالمة" />
        </div>
      )}

      {QA_CRITERIA.map((c) => (
        <label key={c.key} className="field cc-criterion">
          <span
            className="label row"
            style={{ justifyContent: "space-between" }}
          >
            <span>
              {c.label} <span className="tiny mute">({c.weight} نقطة)</span>
            </span>
            <span className="num">{scores[c.key]}</span>
          </span>
          <input
            type="range"
            min={0}
            max={100}
            step={5}
            value={scores[c.key]}
            onChange={(e) =>
              setScores((s) => ({ ...s, [c.key]: Number(e.target.value) }))
            }
          />
        </label>
      ))}

      <label className="field">
        <span className="label">ملاحظات للموظف — يصل بها إشعار</span>
        <textarea
          className="textarea"
          rows={3}
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          placeholder="ما أُجيد، وما يحتاج تدريبًا…"
        />
      </label>
    </Modal>
  );
}
